// chemistryGCSE.js — AQA-aligned KS4 Chemistry (Y10/Y11). Sections C1-C10.
// Compact factory matching biologyGCSE.js pattern.

const gt = (id, slug, title, c) => ({
  id, slug, title,
  difficulty: c.tier || 'foundation',
  pageRef: c.pageRef || '',
  ...(c.diagram ? { diagram: c.diagram } : {}),
  learn: {
    ...(c.intro ? { explanation: c.intro } : {}),
    ...(c.chunks ? { chunks: c.chunks } : {}),
    keyPoints: c.keyPoints || [],
    ...(c.we ? { workedExamples: Array.isArray(c.we) ? c.we : [c.we] } : {}),
    ...(c.whyItWorks ? { whyItWorks: c.whyItWorks } : {}),
  },
  glossary: c.glossary || [],
  ...(c.rp ? { requiredPractical: c.rp } : {}),
  ...(c.examQ ? { examQuestion: c.examQ } : {}),
  ...(c.examinerInsights ? { examinerInsights: c.examinerInsights } : {}),
  practice: {
    warmUp: c.practice?.warmUp || [],
    level1: c.practice?.level1 || [],
    level2: c.practice?.level2 || [],
    level3: c.practice?.level3 || [],
  },
});

const tQ = (id, q, a, h1, h2, h3) => ({ id, type: 'text', question: q, answer: a, hint1: h1, hint2: h2, hint3: h3 });
const nQ = (id, q, a, h1, h2, h3) => ({ id, type: 'number', question: q, answer: a, hint1: h1, hint2: h2, hint3: h3 });
const cQ = (id, q, opts, a, h1, h2, h3) => ({ id, type: 'choice', question: q, options: opts, answer: a, hint1: h1, hint2: h2, hint3: h3 });

// =============================================================
// C1 — Atomic Structure & Periodic Table
// =============================================================
export const c1AtomicStructureTopics = [
  gt('gcse-c1-1', 'atoms-elements-compounds', 'Atoms, Elements & Compounds', {
    pageRef: 'C1.1',
    intro: 'Everything is made of atoms. An element contains one type of atom (e.g. iron, Fe). A compound contains atoms of two or more elements chemically bonded in fixed proportions (e.g. water H₂O). A mixture contains different substances NOT chemically combined — components keep their properties and can be separated physically. Symbols use 1 or 2 letters from the Latin name; formulae use subscripts to show ratios (CO₂ = 1 carbon : 2 oxygen).',
    keyPoints: [
      'Atom: smallest particle; nucleus (protons + neutrons) + electrons in shells',
      'Element: one type of atom; cannot be broken down chemically',
      'Compound: 2+ elements chemically bonded in fixed ratio',
      'Mixture: substances not chemically combined; can be separated physically',
      'Atomic radius ≈ 0.1 nm (1 × 10⁻¹⁰ m); nucleus radius ≈ 1 × 10⁻¹⁴ m',
    ],
    glossary: [
      { term: 'Atom', definition: 'Smallest part of an element that can exist.' },
      { term: 'Element', definition: 'A pure substance made of only one kind of atom.' },
      { term: 'Compound', definition: 'Substance formed when 2+ elements bond chemically in fixed ratio.' },
      { term: 'Mixture', definition: 'Two or more substances not chemically combined.' },
    ],
    practice: {
      warmUp: [
        cQ('c1-1.w1', 'Water (H₂O) is:', ['Element', 'Compound', 'Mixture', 'Atom'], 'Compound', '2 elements bonded', 'Fixed ratio', 'Chemically combined'),
        nQ('c1-1.w2', 'Atomic radius is approximately 1 × 10⁻ⁿ m. What is n?', '10', 'About 0.1 nm', 'Negative power', '2 digits'),
      ],
      level1: [
        cQ('c1-1.1', 'Which is a mixture?', ['Pure water', 'Iron', 'Air', 'Carbon dioxide'], 'Air', 'Multiple gases', 'Not bonded', 'N₂ + O₂ + Ar'),
      ],
      level2: [
        tQ('c1-1.2', 'Smallest part of an element that can exist is an ___.', 'atom', '4 letters', 'A___', 'Greek "indivisible"'),
      ],
    },
  }),

  gt('gcse-c1-2', 'separating-mixtures', 'Separating Mixtures', {
    pageRef: 'C1.2',
    intro: 'Mixtures are separated by physical means. Filtration: insoluble solid from liquid (mud from water). Crystallisation: dissolved soluble solid from solution (salt from sea water; gentle heating, slow cooling). Simple distillation: liquid from solution (pure water from salt water) — boil + condense. Fractional distillation: separating mixed liquids by boiling point (ethanol from water; crude oil into fractions). Chromatography: separates dissolved substances by how strongly they bind to a stationary phase.',
    keyPoints: [
      'Filtration: insoluble solid from liquid',
      'Crystallisation: soluble solid from solution (slow evaporation/cooling)',
      'Simple distillation: pure liquid from solution',
      'Fractional distillation: separates miscible liquids by boiling point',
      'Chromatography: dissolved substances by relative retention',
      'Rf value = distance moved by spot ÷ distance moved by solvent',
    ],
    we: {
      problem: 'A spot moves 4.5 cm; the solvent front moves 9.0 cm. Calculate the Rf value.',
      steps: [
        { step: 1, instruction: 'Rf formula', working: 'Rf = spot ÷ solvent' },
        { step: 2, instruction: 'Substitute', working: '4.5 ÷ 9.0' },
        { step: 3, instruction: 'Evaluate', working: '= 0.5' },
      ],
      answer: 'Rf = 0.5 (no units)',
      commonMistake: 'Including units — Rf is a ratio of two distances, so it has none.',
    },
    glossary: [
      { term: 'Filtration', definition: 'Separating insoluble solid from liquid using a porous filter.' },
      { term: 'Crystallisation', definition: 'Producing solid crystals from a solution by evaporating solvent.' },
      { term: 'Distillation', definition: 'Separating substances by boiling and condensing.' },
      { term: 'Chromatography', definition: 'Separating dissolved substances based on movement through a stationary phase.' },
      { term: 'Rf value', definition: 'Distance spot travelled ÷ distance solvent travelled. Always between 0 and 1.' },
    ],
    practice: {
      warmUp: [
        cQ('c1-2.w1', 'Sand from water is best separated by:', ['Distillation', 'Filtration', 'Crystallisation', 'Chromatography'], 'Filtration', 'Insoluble', 'Filter paper', 'Solid + liquid'),
        nQ('c1-2.w2', 'Rf when spot moves 6 cm and solvent 12 cm. Give as decimal.', '0.5', '6/12', 'Half', 'Decimal'),
      ],
      level1: [
        cQ('c1-2.1', 'Best for separating ethanol from water?', ['Filtration', 'Simple distillation', 'Fractional distillation', 'Chromatography'], 'Fractional distillation', 'Both liquids miscible', 'Different boiling points', 'Column needed'),
      ],
      level2: [
        cQ('c1-2.2', 'Crystallisation works because…', ['Solute decomposes', 'Solvent evaporates leaving solute crystals', 'Solute boils first', 'Pressure changes'], 'Solvent evaporates leaving solute crystals', 'Slow process', 'Pure crystals form', 'Standard procedure'),
      ],
    },
  }),

  gt('gcse-c1-3', 'history-atom', 'History of the Atom', {
    pageRef: 'C1.3',
    intro: 'Models of the atom evolved with new evidence. Dalton (1803): solid indivisible spheres. JJ Thomson (1897): "plum pudding" — sphere of positive charge with electrons embedded (after discovering electron). Rutherford (1909): alpha-particle scattering experiment showed atoms are mostly empty space with a tiny dense positive nucleus. Bohr (1913): electrons orbit at fixed energy levels (shells). Chadwick (1932): discovered the neutron. Each model explained more evidence than the last.',
    keyPoints: [
      'Dalton: indivisible spheres',
      'Thomson: plum pudding (positive sphere, embedded electrons)',
      'Rutherford: alpha scattering → nucleus is small + dense + positive',
      'Bohr: electrons in fixed shells/energy levels',
      'Chadwick: discovered neutrons',
      'Each new model explained evidence the previous could not',
    ],
    glossary: [
      { term: 'Plum pudding model', definition: 'Thomson\'s model: positive sphere with embedded electrons.' },
      { term: 'Alpha scattering', definition: 'Rutherford experiment showing atoms are mostly empty space with a tiny nucleus.' },
      { term: 'Bohr model', definition: 'Electrons orbit nucleus at fixed energy levels (shells).' },
    ],
    practice: {
      warmUp: [
        cQ('c1-3.w1', 'Which scientist discovered the nucleus?', ['Dalton', 'Thomson', 'Rutherford', 'Chadwick'], 'Rutherford', 'Alpha scattering', 'Mostly empty space', 'Gold foil experiment'),
      ],
      level1: [
        tQ('c1-3.1', 'Chadwick discovered the ___.', 'neutron', '7 letters', 'Neutral nuclear particle', 'N______'),
      ],
      level2: [
        cQ('c1-3.2', 'Why was the plum pudding model rejected?', ['Too simple', 'Could not explain alpha scattering', 'No evidence for electrons', 'Lacked colour'], 'Could not explain alpha scattering', 'Rutherford\'s experiment', 'Particles bounced back', 'Required nucleus'),
      ],
    },
  }),

  gt('gcse-c1-4', 'atomic-structure-isotopes', 'Atomic Structure & Isotopes', {
    pageRef: 'C1.4',
    intro: 'An atom has protons (positive, in nucleus), neutrons (no charge, in nucleus) and electrons (negative, in shells). Atomic number = number of protons (defines the element). Mass number = protons + neutrons. Atoms of the same element with different numbers of neutrons are isotopes (e.g. ¹²C and ¹⁴C). Relative atomic mass (Aᵣ) is the average mass of an element\'s isotopes weighted by abundance — that\'s why some Aᵣ values are not integers.',
    keyPoints: [
      'Proton: +1 charge, mass 1, in nucleus',
      'Neutron: 0 charge, mass 1, in nucleus',
      'Electron: -1 charge, mass 1/2000, in shells',
      'Atomic number = number of protons (defines element)',
      'Mass number = protons + neutrons',
      'Isotopes: same element, different mass number (different neutrons)',
      'Aᵣ = weighted average mass of isotopes',
    ],
    we: {
      problem: 'Chlorine has two isotopes: ³⁵Cl (75% abundance) and ³⁷Cl (25%). Calculate the relative atomic mass.',
      steps: [
        { step: 1, instruction: 'Multiply mass × % for each', working: '35 × 75 = 2625; 37 × 25 = 925' },
        { step: 2, instruction: 'Sum and divide by 100', working: '(2625 + 925) ÷ 100' },
        { step: 3, instruction: 'Evaluate', working: '3550 ÷ 100 = 35.5' },
      ],
      answer: 'Aᵣ = 35.5',
      commonMistake: 'Treating it as a simple average (taking (35 + 37) ÷ 2 = 36) instead of weighting by abundance.',
    },
    glossary: [
      { term: 'Proton', definition: 'Positively charged particle in the nucleus; relative mass 1.' },
      { term: 'Neutron', definition: 'Neutral particle in the nucleus; relative mass 1.' },
      { term: 'Electron', definition: 'Negatively charged particle in shells; relative mass ~1/2000.' },
      { term: 'Atomic number', definition: 'Number of protons; defines the element.' },
      { term: 'Mass number', definition: 'Protons + neutrons.' },
      { term: 'Isotope', definition: 'Atom of an element with a different number of neutrons.' },
    ],
    examQ: {
      prompt: 'Boron has two isotopes: ¹⁰B (20% abundance) and ¹¹B (80%). Calculate the relative atomic mass to 1 d.p. (3 marks)',
      maxMarks: 3,
      markSchemePoints: [
        'Multiply each mass by abundance: 10 × 20 = 200; 11 × 80 = 880',
        'Sum: 200 + 880 = 1080',
        'Divide by 100: Aᵣ = 10.8',
      ],
    },
    examinerInsights: {
      whyMarksLost: [
        'Treating it as a simple average ((10+11)/2 = 10.5) instead of weighting by abundance.',
        'Forgetting the final division by 100 (the percentages must sum to 100, not 1).',
        'Rounding the final answer to a whole number — Aᵣ values are usually given to 1 d.p.',
      ],
      whatExaminersWant: [
        'Two visible multiplications + one sum + one division — the four discrete steps.',
        'Final answer as a decimal with units if requested (Aᵣ has none).',
        'Working that any examiner can follow without scratching their head.',
      ],
      commonTraps: [
        'Reading the percentages as decimals (0.20 vs 20) — keep them consistent and divide accordingly.',
        'Forgetting that the heavier isotope has the larger contribution, so Aᵣ should be CLOSER to that isotope mass.',
      ],
      gradeNineMove: 'Sanity-check with the abundance: if the more abundant isotope is the heavier one, your Aᵣ MUST be closer to that mass. Calling out this check on the page is worth a mark in some mark schemes.',
    },
    practice: {
      warmUp: [
        nQ('c1-4.w1', 'Atomic number of an element with 11 protons?', '11', '= protons', 'Sodium', '2 digits'),
        cQ('c1-4.w2', 'Isotopes have same number of:', ['Neutrons', 'Protons', 'Mass', 'Energy levels'], 'Protons', 'Same element', 'Z is same', 'Atomic number'),
      ],
      level1: [
        nQ('c1-4.1', 'Mass number of an atom with 6 protons + 8 neutrons?', '14', 'Sum p+n', '6+8', '2 digits'),
        nQ('c1-4.2', 'Aᵣ of element with 50% mass-24 and 50% mass-26 isotopes?', '25', '(24+26)/2', 'Equal abundance', '25'),
      ],
      level2: [
        nQ('c1-4.3', 'Aᵣ of bromine: ⁷⁹Br (50%), ⁸¹Br (50%). Answer to 1 dp.', '80', 'Equal abundance', '(79+81)/2', '2 digits'),
      ],
    },
  }),

  gt('gcse-c1-5', 'electronic-configuration', 'Electronic Configuration', {
    pageRef: 'C1.5',
    intro: 'Electrons occupy shells (energy levels) around the nucleus. The first shell holds up to 2 electrons; second shell up to 8; third up to 8 (for the first 20 elements). Configuration is written like 2,8,8,1 (potassium, K, atomic number 19). Number of outer-shell electrons determines chemical reactivity and group placement in the Periodic Table. Group number = number of outer electrons (except Group 0/8: full shell). Period number = number of occupied shells.',
    keyPoints: [
      'Shells fill: 2, 8, 8 (first 20 elements)',
      'Configuration: comma-separated, e.g. 2,8,1 = sodium',
      'Outer-shell electrons = group number (except 0)',
      'Number of shells = period number',
      'Full outer shell = unreactive (noble gases, group 0)',
    ],
    we: {
      problem: 'Write the electronic configuration of magnesium (atomic number 12) and predict its group + period.',
      steps: [
        { step: 1, instruction: 'Total electrons = atomic number', working: '12 electrons' },
        { step: 2, instruction: 'Fill: 2, then 8, then remainder', working: '2 + 8 + 2 = 12' },
        { step: 3, instruction: 'Read group + period', working: 'Outer = 2 → Group 2; 3 shells → Period 3' },
      ],
      answer: '2,8,2 — Group 2, Period 3',
    },
    glossary: [
      { term: 'Shell', definition: 'Energy level around the nucleus that electrons occupy.' },
      { term: 'Group', definition: 'Vertical column of the Periodic Table; same number of outer electrons.' },
      { term: 'Period', definition: 'Horizontal row; same number of occupied shells.' },
      { term: 'Noble gas', definition: 'Group 0 element with full outer shell; very unreactive.' },
    ],
    practice: {
      warmUp: [
        tQ('c1-5.w1', 'Electronic configuration of sodium (atomic no 11)?', '2,8,1', '11 electrons', '2+8+1', 'Three numbers'),
        nQ('c1-5.w2', 'Maximum electrons in first shell?', '2', 'Smallest shell', 'Innermost', 'Just 2'),
      ],
      level1: [
        tQ('c1-5.1', 'Electronic configuration of chlorine (Z=17)?', '2,8,7', '17 electrons', '2+8+7', 'Three numbers'),
        nQ('c1-5.2', 'Group of an element with config 2,8,8,2?', '2', 'Outer = group', '2 outer electrons', 'Single digit'),
      ],
      level2: [
        nQ('c1-5.3', 'Period of element with config 2,8,5?', '3', 'Number of shells', '3 shells', 'Single digit'),
      ],
    },
  }),

  gt('gcse-c1-6', 'periodic-table-development', 'Periodic Table & Mendeleev', {
    pageRef: 'C1.6',
    intro: 'Mendeleev (1869) ordered known elements by atomic mass and grouped those with similar properties. Crucially, he LEFT GAPS for undiscovered elements and predicted their properties — gallium and germanium were later discovered with properties he predicted. The modern Periodic Table orders by ATOMIC NUMBER (not mass) — fixes anomalies like Te/I ordering and is consistent with electronic configuration.',
    keyPoints: [
      'Mendeleev (1869): ordered by atomic mass; left gaps for undiscovered',
      'Predicted properties of "eka-silicon" (later germanium) accurately',
      'Modern table: ordered by atomic number',
      'Group = column (same outer electrons); Period = row',
      'Metals on left, non-metals on right',
    ],
    glossary: [
      { term: 'Mendeleev', definition: 'Russian chemist who developed the modern Periodic Table.' },
      { term: 'Atomic number', definition: 'Number of protons; modern ordering principle.' },
    ],
    practice: {
      warmUp: [
        cQ('c1-6.w1', 'Modern table is ordered by:', ['Atomic mass', 'Atomic number', 'Density', 'Discovery date'], 'Atomic number', 'Number of protons', 'Z order', 'Modern principle'),
      ],
      level1: [
        cQ('c1-6.1', 'Why did Mendeleev leave gaps?', ['Boredom', 'For undiscovered elements', 'Page space', 'No reason'], 'For undiscovered elements', 'Predicted new ones', 'Periodicity worked better', 'Eka-silicon = germanium'),
      ],
      level2: [
        tQ('c1-6.2', 'Vertical columns of the Periodic Table are called ___.', 'groups', '6 letters', 'G_____', 'Same outer electrons'),
      ],
    },
  }),

  gt('gcse-c1-7', 'group-1-alkali-metals', 'Group 1 — Alkali Metals', {
    pageRef: 'C1.7',
    intro: 'Group 1 metals (Li, Na, K, Rb, Cs) all have ONE outer electron, so they react in similar ways. They\'re soft (cut with a knife), low-density (Li, Na, K float on water), and very reactive. With water → metal hydroxide + hydrogen (alkaline solution). Reactivity INCREASES down the group: outer electron is further from nucleus → easier to lose. Lithium fizzes; sodium melts to a ball and races; potassium ignites with a lilac flame.',
    keyPoints: [
      'Group 1 = 1 outer electron',
      'Soft, low density (Li, Na, K float on water)',
      'React with water → metal hydroxide + H₂',
      'Reactivity INCREASES down the group',
      'Reason: outer electron further from nucleus → easier to lose',
      '2Na + 2H₂O → 2NaOH + H₂',
    ],
    examQ: {
      prompt: 'Explain why caesium is more reactive than sodium when added to water. Use ideas about electron arrangement. (4 marks)',
      maxMarks: 4,
      markSchemePoints: [
        'Both have ONE electron in their outer shell',
        'Reactivity depends on how easily the outer electron is lost',
        'Caesium has more shells than sodium / outer electron is further from nucleus',
        'Outer electron is more shielded / less attracted, so easier to lose / so caesium reacts more vigorously',
      ],
    },
    glossary: [
      { term: 'Alkali metal', definition: 'A Group 1 element; reacts with water to make an alkaline solution.' },
      { term: 'Reactivity', definition: 'How readily an element undergoes a chemical reaction.' },
    ],
    practice: {
      warmUp: [
        cQ('c1-7.w1', 'Group 1 metals + water →', ['Metal oxide', 'Metal hydroxide + H₂', 'Salt + Cl₂', 'No reaction'], 'Metal hydroxide + H₂', 'Alkaline solution', 'Hydrogen released', 'Standard equation'),
        cQ('c1-7.w2', 'Most reactive Group 1 metal listed:', ['Li', 'Na', 'K', 'Cs'], 'Cs', 'Bottom of group', 'Caesium', 'Most reactive'),
      ],
      level1: [
        cQ('c1-7.1', 'Reactivity in Group 1 going DOWN:', ['Decreases', 'Increases', 'Stays same', 'Disappears'], 'Increases', 'Easier to lose electron', 'More shielding', 'Trend'),
      ],
      level2: [
        tQ('c1-7.2', 'Sodium + water → sodium hydroxide + ___.', 'hydrogen', '8 letters', 'H_______', 'Diatomic gas'),
      ],
    },
  }),

  gt('gcse-c1-8', 'group-7-halogens', 'Group 7 — Halogens', {
    pageRef: 'C1.8',
    intro: 'Group 7 (F, Cl, Br, I) all have 7 outer electrons. Diatomic molecules (Cl₂, Br₂, I₂). Down the group: melting + boiling points INCREASE (larger molecules, stronger intermolecular forces). Reactivity DECREASES down the group: gaining an electron becomes harder as outer shell gets further from nucleus. A more reactive halogen displaces a less reactive one from its salt: Cl₂ + 2KBr → 2KCl + Br₂ (chlorine is more reactive than bromine).',
    keyPoints: [
      'Group 7 = 7 outer electrons; diatomic molecules',
      'Trend: BP/MP increase down the group (Cl₂ gas, Br₂ liquid, I₂ solid)',
      'Reactivity DECREASES down the group',
      'Reason: outer shell further → harder to gain an electron',
      'Displacement: more reactive halogen displaces less reactive one',
      'Cl₂ + 2NaBr → 2NaCl + Br₂',
    ],
    glossary: [
      { term: 'Halogen', definition: 'Group 7 element; non-metal with 7 outer electrons.' },
      { term: 'Displacement', definition: 'A more reactive element takes the place of a less reactive one in a compound.' },
    ],
    practice: {
      warmUp: [
        cQ('c1-8.w1', 'Halogens form what kind of molecules?', ['Single atoms', 'Diatomic', 'Triatomic', 'Polymers'], 'Diatomic', 'Cl₂ etc', 'Pair up', '2 atoms'),
        cQ('c1-8.w2', 'Reactivity DOWN Group 7:', ['Increases', 'Decreases', 'Same', 'Disappears'], 'Decreases', 'Opposite of Group 1', 'Harder to gain electron', 'Trend'),
      ],
      level1: [
        cQ('c1-8.1', 'Cl₂ + 2KBr → ?', ['No reaction', '2KCl + Br₂', '2KCl + 2Br', 'KCl₂ + Br'], '2KCl + Br₂', 'Displacement', 'Cl more reactive', 'Br released'),
      ],
      level2: [
        cQ('c1-8.2', 'Why does iodine have higher MP than fluorine?', ['Smaller molecule', 'Larger molecule = stronger intermolecular forces', 'More electrons donated', 'Lower atomic number'], 'Larger molecule = stronger intermolecular forces', 'More electrons', 'More van der Waals', 'Bigger I₂'),
      ],
    },
  }),

  gt('gcse-c1-9', 'group-0-noble-gases', 'Group 0 — Noble Gases', {
    pageRef: 'C1.9',
    intro: 'Group 0 (helium, neon, argon, krypton, xenon, radon) have FULL outer shells (He = 2, others = 8). This makes them extremely UNREACTIVE — no need to gain or lose electrons. Boiling points increase down the group as atoms get larger. Uses: helium in balloons (low density, non-flammable), argon in light bulbs (no reaction with filament), neon in lights, xenon in headlights.',
    keyPoints: [
      'Full outer shell → unreactive ("inert")',
      'Helium: 2 outer; rest: 8 outer',
      'BPs INCREASE down the group',
      'Uses: He balloons, Ar in bulbs/welding, Ne signs, Xe headlights',
    ],
    glossary: [
      { term: 'Noble gas', definition: 'Group 0 element; full outer shell; unreactive.' },
    ],
    practice: {
      warmUp: [
        cQ('c1-9.w1', 'Why are noble gases unreactive?', ['Tiny atoms', 'Full outer shell', 'No nucleus', 'No electrons'], 'Full outer shell', 'No need to react', 'Stable configuration', 'Octet rule'),
      ],
      level1: [
        tQ('c1-9.1', 'Gas used in filament light bulbs is ___.', 'argon', '5 letters', 'A____', 'Group 0'),
      ],
      level2: [
        cQ('c1-9.2', 'Helium has FULL outer shell with how many electrons?', ['8', '7', '2', '0'], '2', 'First shell only holds 2', 'Special case', 'Smallest noble gas'),
      ],
    },
  }),
];

// =============================================================
// C2 — Bonding, Structure & Properties of Matter
// =============================================================
export const c2BondingTopics = [
  gt('gcse-c2-1', 'ionic-bonding', 'Ionic Bonding', {
    pageRef: 'C2.1',
    intro: 'Ionic bonding occurs between metals and non-metals. Metal atoms LOSE outer electrons (becoming positive cations); non-metal atoms GAIN those electrons (becoming negative anions). Strong electrostatic attraction between oppositely charged ions = ionic bond. Group 1 → 1+, Group 2 → 2+, Group 7 → 1−, Group 6 → 2−. Ions form giant 3D ionic lattices held together in all directions. Example: Na (2,8,1) → Na⁺ (2,8); Cl (2,8,7) → Cl⁻ (2,8,8); together = NaCl.',
    keyPoints: [
      'Metal LOSES electrons → positive ion (cation)',
      'Non-metal GAINS electrons → negative ion (anion)',
      'Group 1 → 1+; Group 2 → 2+; Group 6 → 2−; Group 7 → 1−',
      'Ionic bond = electrostatic attraction between oppositely charged ions',
      'Forms giant 3D ionic lattice',
      'Both ions achieve full outer shell (octet)',
    ],
    we: {
      problem: 'Explain how magnesium chloride (MgCl₂) is formed from atoms.',
      steps: [
        { step: 1, instruction: 'Mg electron config', working: 'Mg = 2,8,2 — needs to lose 2 electrons' },
        { step: 2, instruction: 'Cl electron config', working: 'Cl = 2,8,7 — needs to gain 1 electron each' },
        { step: 3, instruction: 'Electron transfer', working: '1 Mg loses 2e⁻ to 2 Cl atoms (one each)' },
        { step: 4, instruction: 'Resulting ions', working: 'Mg²⁺ + 2 Cl⁻; ionic bonds form lattice MgCl₂' },
      ],
      answer: 'Mg loses 2 electrons → Mg²⁺; each Cl gains 1 → 2 Cl⁻; ratio 1:2 → MgCl₂.',
    },
    glossary: [
      { term: 'Ion', definition: 'A charged atom (gained or lost electrons).' },
      { term: 'Cation', definition: 'A positive ion (lost electrons).' },
      { term: 'Anion', definition: 'A negative ion (gained electrons).' },
      { term: 'Ionic bond', definition: 'Strong electrostatic attraction between oppositely charged ions.' },
      { term: 'Lattice', definition: 'Regular 3D arrangement of ions in an ionic compound.' },
    ],
    practice: {
      warmUp: [
        cQ('c2-1.w1', 'Ionic bonds form between:', ['Two metals', 'Two non-metals', 'Metal + non-metal', 'Two noble gases'], 'Metal + non-metal', 'Transfer of electrons', 'Standard rule', 'Cation + anion'),
        nQ('c2-1.w2', 'Charge of a Group 2 metal ion?', '2', 'Loses 2 electrons', 'Mg²⁺', 'Single digit'),
      ],
      level1: [
        cQ('c2-1.1', 'Sodium loses 1 electron to form:', ['Na⁻', 'Na⁺', 'Na²⁺', 'Na²⁻'], 'Na⁺', 'Lost = positive', 'Group 1', '+1'),
      ],
      level2: [
        tQ('c2-1.2', 'Formula for calcium chloride? (no spaces)', 'cacl2', 'Ca²⁺ + 2 Cl⁻', 'Balance charges', 'Cl₂ part'),
      ],
    },
  }),

  gt('gcse-c2-2', 'covalent-bonding', 'Covalent Bonding', {
    pageRef: 'C2.2',
    intro: 'Covalent bonds form between non-metal atoms by SHARING pairs of electrons — both atoms achieve a full outer shell. Examples: H₂ (one shared pair), HCl, H₂O (oxygen shares with 2 H), CH₄ (C shares with 4 H), CO₂ (double bonds — two shared pairs each), N₂ (triple bond). Simple molecular compounds have weak intermolecular forces between molecules, so they have low melting/boiling points and DON\'T conduct electricity.',
    keyPoints: [
      'Covalent: 2+ non-metals SHARE electron pairs',
      'Each shared pair = 1 covalent bond',
      'Double bond = 2 shared pairs (e.g. O=O); Triple = 3 (e.g. N≡N)',
      'Simple molecules: low MP/BP (weak forces between molecules, NOT bonds)',
      'Don\'t conduct (no charged particles free to move)',
      'Larger molecules → stronger intermolecular forces → higher MP/BP',
    ],
    glossary: [
      { term: 'Covalent bond', definition: 'A shared pair of electrons between two atoms.' },
      { term: 'Simple molecule', definition: 'A small group of atoms covalently bonded; e.g. H₂O.' },
      { term: 'Intermolecular force', definition: 'Weak force BETWEEN molecules (not the same as the covalent bond inside).' },
    ],
    practice: {
      warmUp: [
        cQ('c2-2.w1', 'Covalent bonds form between:', ['Metal + non-metal', 'Non-metals', 'Two metals', 'Noble gases'], 'Non-metals', 'Sharing electrons', 'Both want electrons', 'Same property'),
        nQ('c2-2.w2', 'Pairs of electrons shared in O=O?', '2', 'Double bond', '2 pairs', 'Single digit'),
      ],
      level1: [
        cQ('c2-2.1', 'Why do simple molecules have low MP?', ['Weak covalent bonds', 'Weak forces BETWEEN molecules', 'No bonds', 'Heavy molecules'], 'Weak forces BETWEEN molecules', 'Intermolecular forces', 'Not bond strength', 'Common confusion'),
      ],
      level2: [
        cQ('c2-2.2', 'Simple molecules conduct electricity?', ['Yes', 'No', 'Sometimes', 'Only in solid state'], 'No', 'No free electrons or ions', 'Neutral molecules', 'Common rule'),
      ],
    },
  }),

  gt('gcse-c2-3', 'metallic-bonding', 'Metallic Bonding', {
    pageRef: 'C2.3',
    intro: 'In metals, atoms are arranged in a regular giant lattice. Outer electrons are DELOCALISED — they\'re free to move throughout the structure as a "sea of electrons". Strong electrostatic attraction between positive metal ions and the sea of electrons = metallic bond. Properties: high MP/BP (strong bonds), good conductors (free electrons carry charge AND heat), malleable + ductile (layers slide without breaking bonds). Pure metals are too soft for many uses → mixed with other elements as alloys.',
    keyPoints: [
      'Metal lattice: positive ions + sea of delocalised electrons',
      'Metallic bond = attraction between ions and sea of electrons',
      'Conducts electricity (free electrons) and heat',
      'Malleable + ductile: layers slide without breaking bonds',
      'Pure metal soft; alloys are harder because layers can\'t slide easily',
    ],
    examQ: {
      prompt: 'Explain why copper is a good electrical conductor and is malleable. (4 marks)',
      maxMarks: 4,
      markSchemePoints: [
        'Copper has a giant metallic structure with delocalised (free) electrons',
        'These electrons can move through the structure carrying charge — good conductor',
        'Atoms / ions are arranged in layers',
        'Layers can slide over each other without breaking the metallic bonds — malleable',
      ],
    },
    glossary: [
      { term: 'Metallic bond', definition: 'Attraction between positive metal ions and a sea of delocalised electrons.' },
      { term: 'Delocalised', definition: 'Free to move (not attached to one atom).' },
      { term: 'Alloy', definition: 'Mixture of a metal with at least one other element.' },
      { term: 'Malleable', definition: 'Can be hammered into shape without breaking.' },
    ],
    practice: {
      warmUp: [
        cQ('c2-3.w1', 'Metals conduct because:', ['Lots of ions', 'Delocalised electrons can move', 'Layered structure', 'Magnetic'], 'Delocalised electrons can move', 'Sea of electrons', 'Carry charge', 'Free electrons'),
      ],
      level1: [
        tQ('c2-3.1', 'A mixture of a metal with another element is an ___.', 'alloy', '5 letters', 'A____', 'Steel is one'),
      ],
      level2: [
        cQ('c2-3.2', 'Why are alloys harder than pure metals?', ['More electrons', 'Different-sized atoms stop layers sliding', 'Lower MP', 'Magnetic'], 'Different-sized atoms stop layers sliding', 'Mechanism for hardness', 'Distortion of layers', 'Standard explanation'),
      ],
    },
  }),

  gt('gcse-c2-4', 'states-of-matter', 'States of Matter & Particle Theory', {
    pageRef: 'C2.4',
    intro: 'Particle theory explains the three states. Solids: particles closely packed in regular pattern; vibrate but don\'t move past each other; fixed shape + volume. Liquids: particles still close but can move past each other; fixed volume but take container shape. Gases: particles far apart, fast random motion; fill any container. State changes: melting, boiling, evaporation (gain energy → particles overcome forces); condensing, freezing, sublimation (lose energy). The amount of energy needed depends on the strength of forces holding particles.',
    keyPoints: [
      'Solid: regular, vibrating, fixed shape + volume',
      'Liquid: random close arrangement, can flow, fixed volume',
      'Gas: far apart, fast random, fills container',
      'Heating gives kinetic energy → particles move more, eventually overcome forces',
      'State change happens at fixed temperatures (MP, BP) for pure substances',
      'Stronger intermolecular forces → higher MP/BP',
    ],
    glossary: [
      { term: 'Particle theory', definition: 'Model that all matter is made of particles in motion.' },
      { term: 'Melting point', definition: 'Temperature at which a solid changes to a liquid.' },
      { term: 'Boiling point', definition: 'Temperature at which a liquid changes to a gas.' },
      { term: 'Sublimation', definition: 'Solid changing directly to a gas (e.g. dry ice).' },
    ],
    practice: {
      warmUp: [
        cQ('c2-4.w1', 'In which state are particles farthest apart?', ['Solid', 'Liquid', 'Gas', 'All same'], 'Gas', 'Far apart', 'Random motion', 'Lowest density'),
      ],
      level1: [
        cQ('c2-4.1', 'A pure substance has:', ['Sharp melting point', 'A range of melting temperatures', 'No melting point', 'Variable density'], 'Sharp melting point', 'One value', 'Test for purity', 'Specific BP/MP'),
      ],
      level2: [
        tQ('c2-4.2', 'Going from solid directly to gas is called ___.', 'sublimation', '11 letters', 'Dry ice does this', 'S__________'),
      ],
    },
  }),

  gt('gcse-c2-5', 'giant-covalent-structures', 'Giant Covalent Structures', {
    pageRef: 'C2.5',
    intro: 'Some substances are giant covalent — millions of atoms covalently bonded into one huge network. Examples: diamond (each C bonded to 4 others — very hard, no free electrons → doesn\'t conduct, very high MP), graphite (each C bonded to 3 others in layers — soft as layers slide, conducts because of delocalised electron from 4th bond, used as lubricant + electrode), silicon dioxide (SiO₂ — sand and quartz — very hard, very high MP). Allotropes are different forms of the same element — diamond and graphite are allotropes of carbon.',
    keyPoints: [
      'Diamond: each C → 4 others; very hard; doesn\'t conduct; very high MP',
      'Graphite: each C → 3 others; layers + delocalised electron; soft + conducts',
      'SiO₂ (sand): giant covalent; very hard, very high MP',
      'Allotropes = different forms of same element',
      'All giant covalent = high MP because many strong covalent bonds to break',
    ],
    glossary: [
      { term: 'Giant covalent', definition: 'A huge 3D network of covalently bonded atoms.' },
      { term: 'Allotrope', definition: 'Different physical forms of the same element (e.g. diamond, graphite).' },
    ],
    practice: {
      warmUp: [
        cQ('c2-5.w1', 'Diamond conducts electricity?', ['Yes', 'No', 'Only when hot', 'Only when wet'], 'No', 'No free electrons', 'All 4 bonded', 'Compare graphite'),
      ],
      level1: [
        cQ('c2-5.1', 'Graphite conducts because:', ['It is a metal', '4th electron is delocalised', 'It contains free ions', 'It is liquid at room temp'], '4th electron is delocalised', 'Each C bonded to 3', 'Spare electron', 'Like a metal'),
      ],
      level2: [
        tQ('c2-5.2', 'Different forms of the same element are called ___.', 'allotropes', '10 letters', 'A_________', 'Diamond + graphite'),
      ],
    },
  }),

  gt('gcse-c2-6', 'fullerenes-graphene', 'Fullerenes, Graphene & Nanoparticles', {
    pageRef: 'C2.6',
    tier: 'higher',
    intro: 'Carbon also forms hollow molecular structures: buckminsterfullerene (C₆₀ — football-shaped, 60 carbons), nanotubes (long, hollow cylinders) and graphene (a single layer of graphite — one atom thick). Uses: drug delivery (fullerenes can carry drugs through the body), strong + light materials (nanotubes), conductive coatings (graphene). Nanoparticles (1-100 nm) have a huge surface-area-to-volume ratio, making them very reactive — used in sunscreens, cosmetics, catalysts. Concerns: long-term health effects unknown.',
    keyPoints: [
      'Buckminsterfullerene C₆₀: 60-atom hollow sphere',
      'Nanotubes: hollow cylinders; very strong; high tensile strength',
      'Graphene: single graphite layer; 1 atom thick; conducts well; transparent',
      'Nanoparticles: 1-100 nm; HUGE SA:V ratio',
      'Uses: drug delivery, electronics, strong composites, sunscreens, catalysts',
      'Risks: poorly understood long-term effects on health and environment',
    ],
    glossary: [
      { term: 'Fullerene', definition: 'Hollow molecular form of carbon (e.g. C₆₀).' },
      { term: 'Graphene', definition: 'Single layer of graphite; one atom thick.' },
      { term: 'Nanoparticle', definition: 'Particle 1-100 nm across with very large SA:V ratio.' },
    ],
    practice: {
      warmUp: [
        cQ('c2-6.w1', 'Graphene is:', ['A 3D giant covalent', 'Single layer of graphite', 'A nanoparticle', 'A polymer'], 'Single layer of graphite', '1 atom thick', '2D material', 'Graphite separated'),
      ],
      level1: [
        nQ('c2-6.1', 'Number of carbons in buckminsterfullerene?', '60', 'C₆₀', 'Football-shaped', '2 digits'),
      ],
      level2: [
        cQ('c2-6.2', 'Why are nanoparticles very reactive?', ['Huge SA:V ratio', 'Heavy', 'Always charged', 'Made of metal'], 'Huge SA:V ratio', 'Surface effect', 'Many exposed atoms', 'Standard reason'),
      ],
    },
  }),

  gt('gcse-c2-7', 'polymers-properties', 'Polymers & Their Properties', {
    pageRef: 'C2.7',
    intro: 'Polymers are very large molecules made of repeating units (monomers) joined together. Examples: poly(ethene) from many ethene monomers; PVC, polystyrene, polypropene. Polymers have strong covalent bonds along the chain but weaker INTERMOLECULAR forces between chains. As polymers are large molecules, intermolecular forces are still substantial → polymers are SOLID at room temperature, with relatively high MP. They don\'t conduct (no free charged particles).',
    keyPoints: [
      'Polymer = chain of repeating monomer units',
      'Strong covalent bonds within chain',
      'Weaker intermolecular forces between chains',
      'But large molecules → significant total intermolecular forces → solid at RT',
      'Don\'t conduct',
      'Examples: poly(ethene), PVC, polypropene',
    ],
    glossary: [
      { term: 'Polymer', definition: 'A large molecule made of repeating units.' },
      { term: 'Monomer', definition: 'A small molecule that joins others to form a polymer.' },
      { term: 'Repeating unit', definition: 'The pattern that repeats along a polymer chain.' },
    ],
    practice: {
      warmUp: [
        cQ('c2-7.w1', 'Polymers are:', ['Single small molecules', 'Long chains of monomers', 'Metals', 'Ionic salts'], 'Long chains of monomers', 'Many units joined', 'Big molecules', 'Definition'),
      ],
      level1: [
        tQ('c2-7.1', 'Small molecules that join to make polymers are ___.', 'monomers', '8 letters', 'M_______', 'Mono = one'),
      ],
      level2: [
        cQ('c2-7.2', 'Why are polymers solid at room temperature?', ['Strong covalent bonds within chain', 'Large molecules so substantial intermolecular forces', 'Magnetic', 'Negative charge'], 'Large molecules so substantial intermolecular forces', 'Total IMF significant', 'Bigger molecule = more IMF', 'Despite weak IMF per pair'),
      ],
    },
  }),
];

// =============================================================
// C3 — Quantitative Chemistry
// =============================================================
export const c3QuantitativeTopics = [
  gt('gcse-c3-1', 'conservation-mass-equations', 'Conservation of Mass & Balanced Equations', {
    pageRef: 'C3.1',
    intro: 'In any chemical reaction, atoms are NEVER created or destroyed — they\'re just rearranged. So total mass of reactants = total mass of products (Law of Conservation of Mass). Balanced equations have equal numbers of each type of atom on both sides. Method: count atoms on each side; add coefficients (numbers in front of formulae) to balance — never change subscripts inside formulae.',
    keyPoints: [
      'Mass of reactants = mass of products',
      'Atoms are rearranged, not created/destroyed',
      'Balance with COEFFICIENTS, not by changing formulae',
      'Apparent mass change in some reactions: gas escapes (mass loss) or O₂ added (mass gain)',
    ],
    we: [
      {
        problem: 'Balance: H₂ + O₂ → H₂O',
        steps: [
          { step: 1, instruction: 'Count atoms each side', working: 'Left: 2H + 2O; Right: 2H + 1O' },
          { step: 2, instruction: 'Add coefficient of 2 to H₂O', working: 'H₂ + O₂ → 2H₂O ⇒ Left: 2H + 2O; Right: 4H + 2O' },
          { step: 3, instruction: 'Add coefficient of 2 to H₂', working: '2H₂ + O₂ → 2H₂O ⇒ Both sides: 4H + 2O' },
        ],
        answer: '2H₂ + O₂ → 2H₂O',
      },
    ],
    examQ: {
      prompt: 'A 5.0 g piece of magnesium ribbon is completely burned in air. The product (magnesium oxide) has a mass of 8.3 g. Explain why the mass increased. (3 marks)',
      maxMarks: 3,
      markSchemePoints: [
        'Magnesium reacts with oxygen from the air',
        'Oxygen has joined with the magnesium / atoms have been added to the magnesium',
        'Total mass of reactants (Mg + O) equals total mass of product (MgO) — conservation of mass',
      ],
    },
    glossary: [
      { term: 'Conservation of mass', definition: 'Mass of reactants = mass of products in a chemical reaction.' },
      { term: 'Coefficient', definition: 'Number in front of a chemical formula in an equation.' },
    ],
    practice: {
      warmUp: [
        cQ('c3-1.w1', 'In a balanced equation:', ['Different atoms', 'Same atoms each side', 'Mass is lost', 'New atoms appear'], 'Same atoms each side', 'Conservation', 'Both sides equal', 'Atoms rearranged'),
      ],
      level1: [
        tQ('c3-1.1', 'Balance: Mg + O₂ → MgO. What goes in front of MgO and Mg? (one number, both same)', '2', '2Mg + O₂ → 2MgO', 'Number of MgO = 2', 'Single digit'),
      ],
      level2: [
        cQ('c3-1.2', 'Why might a beaker LOSE mass during a reaction?', ['Atoms destroyed', 'A gas product escapes', 'Reaction ran backwards', 'Conservation broken'], 'A gas product escapes', 'Mass leaves system', 'CO₂ etc', 'Open system'),
      ],
    },
  }),

  gt('gcse-c3-2', 'moles-relative-mass', 'Moles & Relative Formula Mass', {
    pageRef: 'C3.2',
    intro: 'A MOLE is the chemist\'s counting unit: 1 mole = 6.02 × 10²³ particles (Avogadro\'s number). The MASS of 1 mole of a substance in grams equals its relative atomic mass (Aᵣ) or relative formula mass (Mᵣ). Example: Mᵣ of H₂O = (2 × 1) + 16 = 18, so 1 mol of H₂O has mass 18 g. Formula: moles = mass ÷ Mᵣ.',
    keyPoints: [
      '1 mole = 6.02 × 10²³ particles (Avogadro)',
      'Mass of 1 mole = Mᵣ in grams',
      'Mᵣ = sum of all atomic masses in the formula',
      'moles = mass ÷ Mᵣ',
      'mass = moles × Mᵣ',
    ],
    we: [
      {
        problem: 'Calculate the mass of 0.25 mol of CO₂. (Mᵣ data: C = 12, O = 16)',
        steps: [
          { step: 1, instruction: 'Find Mᵣ', working: 'C + 2O = 12 + 32 = 44' },
          { step: 2, instruction: 'mass = moles × Mᵣ', working: '0.25 × 44' },
          { step: 3, instruction: 'Evaluate', working: '= 11 g' },
        ],
        answer: '11 g',
      },
    ],
    glossary: [
      { term: 'Mole', definition: 'Amount of substance containing 6.02 × 10²³ particles.' },
      { term: 'Relative formula mass (Mᵣ)', definition: 'Sum of all relative atomic masses in a formula.' },
      { term: 'Avogadro\'s number', definition: '6.02 × 10²³ — particles per mole.' },
    ],
    examQ: {
      prompt: 'Calculate the number of moles in 50.0 g of calcium carbonate (CaCO₃). (Aᵣ: Ca = 40, C = 12, O = 16) (3 marks)',
      maxMarks: 3,
      markSchemePoints: [
        'Calculate Mᵣ of CaCO₃: 40 + 12 + (3 × 16) = 100',
        'moles = mass ÷ Mᵣ = 50.0 ÷ 100',
        'moles = 0.50 mol',
      ],
    },
    practice: {
      warmUp: [
        nQ('c3-2.w1', 'Mᵣ of H₂O? (H=1, O=16)', '18', '2H + O', '2 + 16', '2 digits'),
        nQ('c3-2.w2', 'Mᵣ of NaCl? (Na=23, Cl=35.5). Round to nearest integer.', '59', 'Sum atoms', '23 + 35.5', '2 digits'),
      ],
      level1: [
        nQ('c3-2.1', 'Moles in 36 g H₂O (Mᵣ = 18)?', '2', '36/18', '36 ÷ 18', '2'),
        nQ('c3-2.2', 'Mass of 0.5 mol CO₂ (Mᵣ = 44)?', '22', '0.5 × 44', 'Half of 44', '2 digits'),
      ],
      level2: [
        nQ('c3-2.3', 'Moles in 25 g CaCO₃ (Mᵣ = 100)?', '0.25', '25/100', 'A quarter', 'Decimal'),
      ],
    },
  }),

  gt('gcse-c3-3', 'percentage-yield-atom-economy', 'Percentage Yield & Atom Economy', {
    pageRef: 'C3.3',
    tier: 'higher',
    intro: 'Yield = how much product is actually made. % Yield = (actual mass of product ÷ theoretical mass) × 100. Less than 100% because: reactions don\'t go to completion, side reactions, losses on transfer. Atom economy = (Mᵣ of useful product ÷ sum of Mᵣ of ALL reactants) × 100. High atom economy = sustainable + economical.',
    keyPoints: [
      '% Yield = (actual ÷ theoretical) × 100',
      'Yields below 100% due to: incomplete reaction, side products, transfer losses',
      'Atom economy = (Mᵣ useful product ÷ sum Mᵣ reactants) × 100',
      'High atom economy = green chemistry / less waste',
    ],
    we: [
      {
        problem: 'A reaction has theoretical yield 8.0 g. Actual yield is 6.0 g. Calculate % yield.',
        steps: [
          { step: 1, instruction: '% yield formula', working: '(actual / theoretical) × 100' },
          { step: 2, instruction: 'Substitute', working: '(6.0 / 8.0) × 100' },
          { step: 3, instruction: 'Evaluate', working: '0.75 × 100 = 75%' },
        ],
        answer: '75%',
      },
    ],
    glossary: [
      { term: 'Yield', definition: 'Amount of product made.' },
      { term: 'Atom economy', definition: 'Percentage of atoms from reactants that end up in the useful product.' },
    ],
    practice: {
      warmUp: [
        nQ('c3-3.w1', 'Actual = 5 g; theoretical = 10 g. % yield?', '50', '5/10 × 100', 'Half', '2 digits'),
      ],
      level1: [
        nQ('c3-3.1', 'Actual = 12 g; theoretical = 16 g. % yield?', '75', '12/16 × 100', '0.75 × 100', '2 digits'),
      ],
      level2: [
        cQ('c3-3.2', 'High atom economy means:', ['Most reactant atoms become useful product', 'Fast reaction', 'Cheap reactants', 'Pure product'], 'Most reactant atoms become useful product', 'Sustainability metric', 'Green chemistry', 'Definition'),
      ],
    },
  }),

  gt('gcse-c3-4', 'concentration-volume', 'Concentration & Volumes', {
    pageRef: 'C3.4',
    intro: 'Concentration = mass of solute (g) ÷ volume of solution (dm³). Or moles ÷ volume = mol/dm³. 1 dm³ = 1000 cm³ = 1 litre. To make a more dilute solution: add more solvent. Solid → solution: dissolve known mass in known volume. Acid-base titrations use known concentration to find an unknown concentration.',
    keyPoints: [
      'Concentration (g/dm³) = mass ÷ volume',
      'Concentration (mol/dm³) = moles ÷ volume',
      '1 dm³ = 1000 cm³',
      'Dilute = add more water; same mass ÷ bigger volume = lower concentration',
    ],
    we: [
      {
        problem: '12.5 g of salt dissolved in 250 cm³ of water. Concentration in g/dm³?',
        steps: [
          { step: 1, instruction: 'Convert volume', working: '250 cm³ = 0.25 dm³' },
          { step: 2, instruction: 'Concentration = mass ÷ volume', working: '12.5 ÷ 0.25' },
          { step: 3, instruction: 'Evaluate', working: '50 g/dm³' },
        ],
        answer: '50 g/dm³',
      },
    ],
    glossary: [
      { term: 'Concentration', definition: 'Mass or moles of solute per unit volume of solution.' },
      { term: 'Solute', definition: 'The substance that is dissolved.' },
      { term: 'Solvent', definition: 'The liquid that does the dissolving.' },
    ],
    practice: {
      warmUp: [
        nQ('c3-4.w1', '20 g in 1 dm³. Concentration g/dm³?', '20', 'Direct division', '20/1', '2 digits'),
        nQ('c3-4.w2', '500 cm³ in dm³?', '0.5', 'Divide by 1000', '500/1000', 'Decimal'),
      ],
      level1: [
        nQ('c3-4.1', '8 g in 200 cm³. Concentration g/dm³?', '40', '200 cm³ = 0.2 dm³; 8/0.2', '8 ÷ 0.2', '2 digits'),
      ],
      level2: [
        nQ('c3-4.2', 'Moles in 250 cm³ of 2.0 mol/dm³ solution?', '0.5', '0.25 × 2', 'Volume × conc', 'Decimal'),
      ],
    },
  }),
];

// =============================================================
// C4 — Chemical Changes
// =============================================================
export const c4ChemicalChangesTopics = [
  gt('gcse-c4-1', 'reactivity-series', 'Reactivity Series of Metals', {
    pageRef: 'C4.1',
    intro: 'Metals can be ranked by how easily they LOSE electrons (react). The reactivity series (most → least): K, Na, Ca, Mg, Al, (C), Zn, Fe, (H), Cu, Ag, Au. Hydrogen and carbon are non-metals included as reference points. A more reactive metal will displace a less reactive metal from a salt solution: Mg + CuSO₄ → MgSO₄ + Cu. Reactivity also explains how metals are extracted: K-Al → electrolysis; Zn-Cu → reduction with carbon.',
    keyPoints: [
      'Reactivity series: K Na Ca Mg Al C Zn Fe H Cu Ag Au',
      'More reactive = more easily loses electrons',
      'Displacement: more reactive metal pushes less reactive metal out of its compound',
      'Above carbon → extracted by electrolysis',
      'Below carbon → extracted by reduction with carbon (e.g. iron from blast furnace)',
    ],
    glossary: [
      { term: 'Reactivity series', definition: 'List of metals in order of how easily they react.' },
      { term: 'Displacement', definition: 'A more reactive element takes the place of a less reactive one.' },
      { term: 'Reduction', definition: 'Loss of oxygen (or gain of electrons).' },
    ],
    practice: {
      warmUp: [
        cQ('c4-1.w1', 'Most reactive metal listed:', ['K', 'Cu', 'Au', 'Fe'], 'K', 'Top of series', 'Group 1', 'Potassium'),
      ],
      level1: [
        cQ('c4-1.1', 'Will Mg displace Cu from CuSO₄?', ['No', 'Yes', 'Only with heat', 'Only with light'], 'Yes', 'Mg more reactive', 'Standard rule', 'Displacement'),
      ],
      level2: [
        cQ('c4-1.2', 'How is aluminium extracted?', ['Reduction with C', 'Electrolysis', 'Mining as element', 'Fermentation'], 'Electrolysis', 'Above carbon', 'Too reactive for C reduction', 'Of molten Al₂O₃'),
      ],
    },
  }),

  gt('gcse-c4-2', 'acids-alkalis-pH', 'Acids, Alkalis & The pH Scale', {
    pageRef: 'C4.2',
    intro: 'Acids release H⁺ ions in water (HCl → H⁺ + Cl⁻). Alkalis are soluble bases that release OH⁻ ions in water (NaOH → Na⁺ + OH⁻). pH scale runs 0-14: pH 0-6 acidic, pH 7 neutral, pH 8-14 alkaline. Each pH unit = 10× change in [H⁺] (logarithmic). Strong acids (HCl, H₂SO₄, HNO₃) fully dissociate; weak acids (ethanoic, citric) only partially. Concentrated/dilute refers to AMOUNT of acid in solution (different from strength).',
    keyPoints: [
      'Acid: pH < 7; releases H⁺ in water',
      'Alkali: pH > 7; releases OH⁻ in water',
      'Neutral: pH = 7',
      'Each pH unit = 10× change in [H⁺]',
      'Strong acid: fully dissociates (HCl, H₂SO₄)',
      'Weak acid: partially dissociates (ethanoic acid)',
      'Strength ≠ concentration',
    ],
    glossary: [
      { term: 'Acid', definition: 'Substance that releases H⁺ ions in water.' },
      { term: 'Alkali', definition: 'Soluble base; releases OH⁻ ions in water.' },
      { term: 'pH scale', definition: 'Logarithmic 0-14 scale of acidity/alkalinity.' },
      { term: 'Strong acid', definition: 'Fully dissociates in water.' },
    ],
    practice: {
      warmUp: [
        cQ('c4-2.w1', 'pH of pure water?', ['0', '3', '7', '14'], '7', 'Neutral', 'Middle of scale', 'No excess H⁺/OH⁻'),
        nQ('c4-2.w2', '[H⁺] difference between pH 1 and pH 3?', '100', '10×10', '2 pH units = 100×', '3 digits'),
      ],
      level1: [
        cQ('c4-2.1', 'A strong acid:', ['Has high concentration', 'Fully dissociates', 'Has low pH always', 'Smells'], 'Fully dissociates', 'Definition of strength', 'Releases all H⁺', 'vs weak'),
      ],
      level2: [
        cQ('c4-2.2', 'pH of 0.1 mol/dm³ HCl approximately?', ['1', '4', '7', '10'], '1', 'Strong acid', 'Fully dissociates', 'pH = -log[H⁺] = -log(0.1)'),
      ],
    },
  }),

  gt('gcse-c4-3', 'neutralisation-titration', 'Neutralisation & Titration (RP1)', {
    pageRef: 'C4.3',
    intro: 'Neutralisation: acid + alkali → salt + water. The H⁺ from the acid joins OH⁻ from the alkali to make water. Titration finds the volume of one solution needed to react exactly with a known volume of another — used to calculate unknown concentrations. Method: known volume of one solution in conical flask, indicator added; the other solution is added from a burette until the colour just changes (end-point).',
    keyPoints: [
      'Acid + alkali → salt + water (neutralisation)',
      'H⁺ + OH⁻ → H₂O (the actual ionic equation)',
      'Titration: precise way to react two solutions',
      'Indicators: methyl orange (red ↔ yellow), phenolphthalein (pink ↔ colourless)',
      'End-point = colour change of indicator',
    ],
    rp: {
      title: 'Required Practical: Preparation of a pure dry sample of a soluble salt by titration',
      aqaRef: 'AQA Required Practical (Chemistry) 1',
      aim: 'Find the concentration of an alkali by titrating against a known acid.',
      method: [
        'Use a pipette to transfer 25.0 cm³ of alkali (e.g. NaOH) into a conical flask.',
        'Add 3 drops of phenolphthalein indicator (alkali turns pink).',
        'Fill a burette with the acid and record initial reading to 0.05 cm³.',
        'Add acid drop-by-drop, swirling, until the pink JUST disappears (end-point).',
        'Record final burette reading.',
        'Repeat until you have at least two concordant readings (within 0.10 cm³).',
        'Calculate concentration using c₁v₁ = c₂v₂.',
      ],
      safety: 'Acids and alkalis can irritate skin/eyes — wear goggles. Glass burette is heavy/fragile.',
      tips: [
        'Read burette at the bottom of the meniscus, eye level.',
        'First titration is rough; calculate mean of CONCORDANT runs only.',
        'Rinse pipette with the solution it will measure (not water) to avoid dilution.',
      ],
    },
    glossary: [
      { term: 'Neutralisation', definition: 'Reaction between acid and alkali to make salt + water.' },
      { term: 'Indicator', definition: 'Substance that changes colour with pH.' },
      { term: 'Titration', definition: 'Method of finding the volume of one solution needed to react with another.' },
      { term: 'End-point', definition: 'Point in a titration when indicator just changes colour.' },
    ],
    practice: {
      warmUp: [
        cQ('c4-3.w1', 'Acid + alkali →', ['Salt + water', 'Hydrogen + oxygen', 'Salt + hydrogen', 'No reaction'], 'Salt + water', 'Standard equation', 'Neutralisation', 'Two products'),
      ],
      level1: [
        cQ('c4-3.1', 'Phenolphthalein in alkali is:', ['Colourless', 'Pink', 'Yellow', 'Red'], 'Pink', 'pH > ~8', 'Alkaline colour', 'Common result'),
      ],
      level2: [
        cQ('c4-3.2', 'Why rinse the pipette with the alkali (not water)?', ['Looks better', 'Water dilutes the alkali', 'Water boils', 'No reason'], 'Water dilutes the alkali', 'Avoids dilution error', 'Standard practice', 'Concentration accuracy'),
      ],
    },
  }),

  gt('gcse-c4-4', 'electrolysis', 'Electrolysis', {
    pageRef: 'C4.4',
    intro: 'Electrolysis splits ionic compounds using electricity. Molten or dissolved ionic compounds conduct because ions are free to move. Positive cathode attracts NEGATIVE ions; positive anode attracts negative ions — wait — convention: positive ions (cations) go to NEGATIVE cathode; negative ions (anions) go to POSITIVE anode. At cathode → reduction (gain electrons); at anode → oxidation (lose electrons). Used to extract reactive metals (Al from molten Al₂O₃) and to electroplate.',
    keyPoints: [
      'Splits ionic compounds using electricity',
      'Positive ions → negative cathode (gain electrons / reduction)',
      'Negative ions → positive anode (lose electrons / oxidation)',
      'Aluminium extraction: molten Al₂O₃ in cryolite',
      'Half-equations: Al³⁺ + 3e⁻ → Al; 2O²⁻ → O₂ + 4e⁻',
      'Aqueous: H⁺ vs metal ion at cathode (less reactive metal preferred); OH⁻ vs halide at anode',
    ],
    glossary: [
      { term: 'Electrolysis', definition: 'Using electricity to break down an ionic compound.' },
      { term: 'Cathode', definition: 'Negative electrode (attracts cations).' },
      { term: 'Anode', definition: 'Positive electrode (attracts anions).' },
      { term: 'Cryolite', definition: 'Mineral added to alumina to lower its melting point during aluminium extraction.' },
    ],
    rp: {
      title: 'Required Practical: Electrolysis of aqueous solutions',
      aqaRef: 'AQA Required Practical (Chemistry) 3',
      aim: 'Investigate what is produced at each electrode when various aqueous ionic solutions are electrolysed.',
      method: [
        'Set up an electrolysis cell with two graphite (inert) electrodes in a beaker.',
        'Connect electrodes to a low-voltage DC power pack.',
        'Half-fill the beaker with the solution to be tested (e.g. copper(II) chloride, sodium chloride, sodium sulfate).',
        'Switch on power; record observations at each electrode for 5 minutes.',
        'Test gases produced: lit splint (squeaky pop = H₂), glowing splint (relights = O₂), damp blue litmus (bleaches = Cl₂).',
        'Repeat with each solution; tabulate cathode/anode products.',
      ],
      safety: 'Chlorine is toxic — work in a fume cupboard or well-ventilated area. Disconnect power before adjusting electrodes.',
      tips: [
        'Carbon/graphite electrodes are INERT — they do not react with the products.',
        'At cathode: less reactive of (H, metal ion) is discharged → so for Cu²⁺ → Cu metal, but for Na⁺ → H₂ gas.',
        'At anode: halide ions (Cl⁻ / Br⁻ / I⁻) preferred over OH⁻; otherwise oxygen produced from OH⁻.',
      ],
    },
    practice: {
      warmUp: [
        cQ('c4-4.w1', 'Positive ions go to:', ['Anode', 'Cathode', 'Both', 'Neither'], 'Cathode', 'Cations to cathode', 'Negative electrode', 'Opposite charges attract'),
      ],
      level1: [
        cQ('c4-4.1', 'Why must the compound be molten or dissolved for electrolysis?', ['Looks nicer', 'Ions need to be free to move', 'Cheaper', 'Faster'], 'Ions need to be free to move', 'Solid ions are fixed', 'Need conduction', 'Mobile charges'),
      ],
      level2: [
        cQ('c4-4.2', 'At cathode in aluminium electrolysis:', ['Al²⁺ + 2e⁻ → Al', 'Al³⁺ + 3e⁻ → Al', 'Al → Al³⁺ + 3e⁻', '2O²⁻ → O₂ + 4e⁻'], 'Al³⁺ + 3e⁻ → Al', 'Reduction', 'Gain electrons', 'Cathode = reduction'),
      ],
    },
  }),
];

// =============================================================
// C5 — Energy Changes
// =============================================================
export const c5EnergyChangesTopics = [
  gt('gcse-c5-1', 'exo-endothermic-reactions', 'Exothermic & Endothermic Reactions', {
    pageRef: 'C5.1',
    intro: 'Exothermic reactions release energy to surroundings (temperature rises) — combustion, neutralisation, oxidation, hand warmers. Endothermic reactions take in energy (temperature drops) — thermal decomposition, photosynthesis, sports cool packs. On a reaction profile, exothermic: reactants ABOVE products (energy released); endothermic: products ABOVE reactants. Activation energy (Eₐ) is the minimum energy needed to start a reaction — the "hump" on a reaction profile.',
    keyPoints: [
      'Exothermic: releases energy → surroundings warm',
      'Endothermic: absorbs energy → surroundings cool',
      'Profile: reactants → products via activation-energy hump',
      'Activation energy = minimum energy to start reaction',
      'Combustion, neutralisation, most oxidation = exothermic',
      'Thermal decomposition, photosynthesis = endothermic',
    ],
    glossary: [
      { term: 'Exothermic', definition: 'Releases energy to surroundings (temperature rises).' },
      { term: 'Endothermic', definition: 'Absorbs energy from surroundings (temperature drops).' },
      { term: 'Activation energy', definition: 'Minimum energy needed to start a reaction.' },
      { term: 'Reaction profile', definition: 'Energy diagram showing reactants, products, and activation energy.' },
    ],
    rp: {
      title: 'Required Practical: Temperature changes — investigating energy changes in reactions',
      aqaRef: 'AQA Required Practical (Chemistry) 4',
      aim: 'Measure the temperature change when different volumes of acid and alkali are mixed, and identify the volumes that give the largest exothermic change.',
      method: [
        'Use a polystyrene cup inside a beaker (insulator) with a lid + thermometer hole.',
        'Measure 30 cm³ of dilute HCl into the cup; record initial temperature.',
        'Add 5 cm³ of NaOH; stir; record max/min temperature.',
        'Repeat, increasing NaOH by 5 cm³ each time up to 40 cm³.',
        'Plot temperature change against volume of alkali added.',
        'Identify volume giving the maximum temperature rise.',
      ],
      safety: 'Acids and alkalis irritate skin/eyes — wear goggles. Polystyrene cup may be fragile.',
      tips: [
        'Polystyrene insulates — minimises heat lost to surroundings.',
        'A LID reduces heat loss further; small thermometer hole only.',
        'Stir BEFORE recording temperature — even mixing matters.',
      ],
    },
    practice: {
      warmUp: [
        cQ('c5-1.w1', 'Combustion is:', ['Endothermic', 'Exothermic', 'Both', 'Neither'], 'Exothermic', 'Releases heat', 'Standard', 'Burning'),
      ],
      level1: [
        cQ('c5-1.1', 'On a reaction profile for an EXOTHERMIC reaction:', ['Products above reactants', 'Reactants above products', 'Same level', 'Vertical line'], 'Reactants above products', 'Energy released', 'Decrease', 'Standard'),
      ],
      level2: [
        tQ('c5-1.2', 'Minimum energy to start a reaction is the ___ energy.', 'activation', '10 letters', 'A_________', 'Hump on profile'),
      ],
    },
  }),

  gt('gcse-c5-2', 'bond-energy-calculations', 'Bond Energy Calculations', {
    pageRef: 'C5.2',
    tier: 'higher',
    intro: 'Energy is needed to BREAK bonds (endothermic) and released when bonds FORM (exothermic). Energy change of a reaction = (bonds broken) − (bonds made). If bonds made > bonds broken: exothermic, negative ΔH. If bonds broken > bonds made: endothermic, positive ΔH. Bond energies given in kJ/mol.',
    keyPoints: [
      'Breaking bonds: endothermic (energy IN)',
      'Making bonds: exothermic (energy OUT)',
      'ΔH = (sum bond energies broken) − (sum bond energies made)',
      'Negative ΔH = exothermic (bonds made > broken)',
      'Positive ΔH = endothermic (bonds broken > made)',
    ],
    we: [
      {
        problem: 'For H₂ + Cl₂ → 2HCl. Bond energies (kJ/mol): H-H = 436; Cl-Cl = 243; H-Cl = 432. Calculate ΔH.',
        steps: [
          { step: 1, instruction: 'Bonds broken (reactants)', working: '1 × H-H + 1 × Cl-Cl = 436 + 243 = 679 kJ' },
          { step: 2, instruction: 'Bonds made (products)', working: '2 × H-Cl = 2 × 432 = 864 kJ' },
          { step: 3, instruction: 'ΔH = broken − made', working: '679 − 864 = −185 kJ/mol' },
        ],
        answer: 'ΔH = −185 kJ/mol (exothermic — bonds made > broken)',
      },
    ],
    glossary: [
      { term: 'Bond energy', definition: 'Energy needed to break 1 mole of a particular bond.' },
      { term: 'ΔH', definition: 'Change in energy of a reaction; negative = exothermic.' },
    ],
    practice: {
      warmUp: [
        cQ('c5-2.w1', 'Breaking a bond:', ['Releases energy', 'Requires energy', 'Neither', 'Always exothermic'], 'Requires energy', 'Endothermic step', 'Pulling apart', 'Need input'),
      ],
      level1: [
        nQ('c5-2.1', 'Bonds broken = 800 kJ; bonds made = 950 kJ. ΔH (kJ)?', '-150', '800 - 950', 'Negative result', 'Exothermic'),
      ],
      level2: [
        cQ('c5-2.2', 'A reaction has ΔH = +120 kJ/mol. It is:', ['Exothermic', 'Endothermic', 'Neither', 'Catalytic'], 'Endothermic', 'Positive sign', 'Energy in', 'Sign convention'),
      ],
    },
  }),
];

// =============================================================
// C6 — Rate & Extent of Chemical Change
// =============================================================
export const c6RateExtentTopics = [
  gt('gcse-c6-1', 'rate-of-reaction', 'Rate of Reaction', {
    pageRef: 'C6.1',
    intro: 'Rate measures how fast a reaction goes. Rate = amount of product made (or reactant used) ÷ time. Faster = bigger rate. Factors that increase rate: higher temperature (faster particles, more energy + more frequent collisions), higher concentration / pressure (more particles in same volume), larger surface area of solid (more exposed atoms), catalyst (lowers Eₐ).',
    keyPoints: [
      'Rate = amount product ÷ time (or reactant ÷ time)',
      'Increase rate by: higher temp, higher concentration, more SA, catalyst',
      'Collision theory: reaction needs particles to collide WITH ENOUGH ENERGY (≥ Eₐ) and CORRECT ORIENTATION',
      'Catalyst: speeds up reaction without being used up; lowers Eₐ',
    ],
    we: [
      {
        problem: '24 cm³ of gas is produced in 60 s. Calculate the mean rate (cm³/s).',
        steps: [
          { step: 1, instruction: 'Rate = volume ÷ time', working: '24 ÷ 60' },
          { step: 2, instruction: 'Evaluate', working: '= 0.4 cm³/s' },
        ],
        answer: '0.4 cm³/s',
      },
    ],
    glossary: [
      { term: 'Rate of reaction', definition: 'How fast reactants are used or products are formed per unit time.' },
      { term: 'Catalyst', definition: 'Substance that speeds up a reaction without being used up; lowers Eₐ.' },
      { term: 'Collision theory', definition: 'Reactions happen when particles collide with sufficient energy and correct orientation.' },
    ],
    rp: {
      title: 'Required Practical: Rates of reaction — effect of concentration',
      aqaRef: 'AQA Required Practical (Chemistry) 5',
      aim: 'Investigate the effect of changing the concentration of a reactant on the rate of a reaction (e.g. sodium thiosulfate + dilute HCl).',
      method: [
        'Place a conical flask on a piece of paper marked with a black cross.',
        'Add 50 cm³ of sodium thiosulfate solution at known concentration.',
        'Add 5 cm³ of dilute HCl, start the timer immediately.',
        'Look DOWN through the flask onto the cross — stop the timer when the cross is no longer visible (sulfur clouds the solution).',
        'Repeat with diluted thiosulfate (40 cm³ thio + 10 cm³ water; 30 cm³ + 20 cm³; etc.) keeping total volume constant.',
        'Tabulate concentration vs time; plot rate (1/time) against concentration.',
      ],
      safety: 'Sulfur dioxide gas is produced — work in a well-ventilated area or fume cupboard. Avoid inhalation, especially for asthma sufferers.',
      tips: [
        'Use the same observer + same lighting throughout — the end-point is subjective.',
        'Total volume constant ensures only concentration changes.',
        'Plot 1/time vs concentration — should be approximately a straight line through origin (rate ∝ concentration).',
      ],
    },
    examQ: {
      prompt: 'Explain why marble chips react more quickly with hydrochloric acid when the chips are crushed into powder. (3 marks)',
      maxMarks: 3,
      markSchemePoints: [
        'Crushing increases the surface area of the marble',
        'More surface area means more marble particles are exposed to acid',
        'More frequent successful collisions per second → faster rate',
      ],
    },
    practice: {
      warmUp: [
        nQ('c6-1.w1', '50 cm³ of gas in 25 s. Mean rate cm³/s?', '2', '50/25', 'Volume/time', '1 digit'),
      ],
      level1: [
        cQ('c6-1.1', 'Why does heating increase rate?', ['Particles bigger', 'Particles move faster + more collisions with sufficient energy', 'Pressure higher', 'Volume bigger'], 'Particles move faster + more collisions with sufficient energy', 'Collision theory', 'Eₐ exceeded more', 'Standard explanation'),
      ],
      level2: [
        cQ('c6-1.2', 'A catalyst:', ['Is used up', 'Lowers Eₐ; not used up', 'Always endothermic', 'Increases ΔH'], 'Lowers Eₐ; not used up', 'Definition', 'Activation energy reduced', 'Recovered after'),
      ],
    },
  }),

  gt('gcse-c6-2', 'reversible-equilibrium', 'Reversible Reactions & Equilibrium', {
    pageRef: 'C6.2',
    tier: 'higher',
    intro: 'Some reactions are reversible: A + B ⇌ C + D. Forward and reverse reactions both happen. In a closed system, eventually rate forward = rate reverse → DYNAMIC EQUILIBRIUM. Le Chatelier\'s principle: if conditions change, equilibrium shifts to oppose the change. ↑ T → shifts in endothermic direction (away from heat). ↑ pressure → shifts to side with FEWER moles of gas. ↑ concentration of A → shifts to make more product.',
    keyPoints: [
      'Reversible: ⇌ symbol',
      'Equilibrium: forward rate = reverse rate (in closed system)',
      'Le Chatelier: equilibrium opposes the change',
      '↑ T → endothermic direction; ↓ T → exothermic',
      '↑ P → fewer-moles side; ↓ P → more-moles side',
      'Catalyst: doesn\'t shift equilibrium; just gets you there faster',
    ],
    glossary: [
      { term: 'Reversible reaction', definition: 'A reaction that can go forwards or backwards.' },
      { term: 'Dynamic equilibrium', definition: 'Forward and reverse rates are equal; concentrations stay constant.' },
      { term: 'Le Chatelier\'s principle', definition: 'A system at equilibrium opposes any change made to it.' },
    ],
    practice: {
      warmUp: [
        cQ('c6-2.w1', 'Symbol for reversible reaction is:', ['→', '⇌', '=', '≠'], '⇌', 'Two arrows', 'Equilibrium symbol', 'Forward + reverse'),
      ],
      level1: [
        cQ('c6-2.1', 'Increasing temperature shifts equilibrium toward:', ['Always forward', 'Endothermic direction', 'Always reverse', 'Doesn\'t shift'], 'Endothermic direction', 'Le Chatelier', 'Opposes heat increase', 'Absorbs added heat'),
      ],
      level2: [
        cQ('c6-2.2', 'Catalyst on equilibrium position:', ['Shifts forward', 'Shifts back', 'No shift; just faster to reach', 'Stops reaction'], 'No shift; just faster to reach', 'Equal effect on both rates', 'Common error', 'Position unchanged'),
      ],
    },
  }),
];

// =============================================================
// C7 — Organic Chemistry
// =============================================================
export const c7OrganicTopics = [
  gt('gcse-c7-1', 'crude-oil-fractional-distillation', 'Crude Oil & Fractional Distillation', {
    pageRef: 'C7.1',
    intro: 'Crude oil is a finite, fossil-fuel mixture of HYDROCARBONS — molecules made of only hydrogen and carbon. ALKANES are saturated hydrocarbons (only single bonds). General formula CₙH₂ₙ₊₂. First four: methane CH₄, ethane C₂H₆, propane C₃H₈, butane C₄H₁₀. Larger alkanes have higher boiling point, more viscous, less flammable. Crude oil is separated by FRACTIONAL DISTILLATION: heated, vapours rise through a column with a temperature gradient (hot at bottom). Fractions: refinery gas, petrol, kerosene, diesel, heavy oil, bitumen.',
    keyPoints: [
      'Crude oil: finite mixture of hydrocarbons',
      'Alkanes: saturated, formula CₙH₂ₙ₊₂',
      'Methane CH₄, ethane C₂H₆, propane C₃H₈, butane C₄H₁₀',
      'Fractional distillation column: hot bottom → cool top',
      'Fractions in order (small/light at top): gases, petrol, kerosene, diesel, fuel oil, bitumen',
      'Larger molecules: higher BP, more viscous, less flammable',
    ],
    glossary: [
      { term: 'Hydrocarbon', definition: 'Compound containing only carbon and hydrogen.' },
      { term: 'Alkane', definition: 'Saturated hydrocarbon, all single bonds, formula CₙH₂ₙ₊₂.' },
      { term: 'Fractional distillation', definition: 'Separation of a mixture of liquids using their different boiling points.' },
      { term: 'Fraction', definition: 'A group of hydrocarbons with similar boiling points collected at the same level of the column.' },
    ],
    practice: {
      warmUp: [
        tQ('c7-1.w1', 'Hydrocarbon with 3 carbons (CₙH₂ₙ₊₂)?', 'propane', '7 letters', 'C₃H₈', 'P______'),
        nQ('c7-1.w2', 'How many H atoms in butane (C₄H₁₀)?', '10', '2(4)+2', '10', '2 digits'),
      ],
      level1: [
        cQ('c7-1.1', 'In fractional distillation, where are the SHORTEST chain hydrocarbons collected?', ['Top of column (cooler)', 'Middle', 'Bottom (hottest)', 'Outside the column'], 'Top of column (cooler)', 'Low BP', 'Smallest molecules', 'Boil first'),
      ],
      level2: [
        cQ('c7-1.2', 'As alkane chain length increases:', ['BP decreases', 'BP increases; viscosity increases; flammability decreases', 'Becomes more flammable', 'Becomes a gas'], 'BP increases; viscosity increases; flammability decreases', 'Standard trend', 'Three-part trend', 'Memorise'),
      ],
    },
  }),

  gt('gcse-c7-2', 'cracking-alkenes', 'Cracking & Alkenes', {
    pageRef: 'C7.2',
    intro: 'Cracking breaks long-chain alkanes into shorter, more useful molecules — alkenes plus shorter alkanes. Why? Demand for petrol > supply from crude oil; long fractions also less useful. Two methods: catalytic cracking (~550°C, zeolite catalyst) and steam cracking (~850°C). Alkenes: UNSATURATED hydrocarbons with C=C double bond. General formula CₙH₂ₙ. Test for alkene: bromine water turns from orange → colourless (decolourised) due to addition reaction across the C=C.',
    keyPoints: [
      'Cracking: long alkane → short alkane + alkene',
      'Reasons: demand mismatch; long fractions less useful',
      'Methods: catalytic (zeolite catalyst, 550°C); steam (850°C)',
      'Alkenes: C=C double bond; CₙH₂ₙ',
      'Bromine water test: alkene decolourises orange bromine water; alkane does not',
    ],
    glossary: [
      { term: 'Cracking', definition: 'Breaking long-chain hydrocarbons into shorter ones.' },
      { term: 'Alkene', definition: 'Unsaturated hydrocarbon with at least one C=C double bond; CₙH₂ₙ.' },
      { term: 'Bromine water test', definition: 'Orange → colourless if alkene present; orange remains if alkane.' },
    ],
    practice: {
      warmUp: [
        cQ('c7-2.w1', 'Alkenes have:', ['Only single bonds', 'A C=C double bond', 'Triple bond', 'No carbon'], 'A C=C double bond', 'Unsaturated', 'Standard definition', 'C=C'),
      ],
      level1: [
        cQ('c7-2.1', 'Cracking C₁₀H₂₂ produces C₈H₁₈ and what alkene?', ['CH₄', 'C₂H₆', 'C₂H₄', 'C₂H₂'], 'C₂H₄', 'Carbons + H balance', 'Ethene', 'CₙH₂ₙ form'),
      ],
      level2: [
        cQ('c7-2.2', 'A student adds bromine water to a hydrocarbon. The orange colour stays. The hydrocarbon must be:', ['An alkene', 'An alkane', 'Pure water', 'Crude oil'], 'An alkane', 'No C=C', 'No reaction', 'Saturated'),
      ],
    },
  }),

  gt('gcse-c7-3', 'alcohols-carboxylic-polymers', 'Alcohols, Carboxylic Acids & Polymers', {
    pageRef: 'C7.3',
    intro: 'Alcohols contain the -OH group. First four: methanol, ethanol, propanol, butanol. Used as fuels and solvents. Ethanol is in alcoholic drinks; made by fermentation of sugars by yeast. Carboxylic acids contain -COOH. First four: methanoic, ethanoic, propanoic, butanoic. Ethanoic acid = vinegar. Polymers are formed by joining many monomer units. Addition polymerisation: alkene monomers (e.g. ethene) join up via opening of C=C → poly(ethene), no other product. Condensation: monomers join with elimination of small molecule (e.g. water).',
    keyPoints: [
      'Alcohols: -OH group; methanol, ethanol, propanol, butanol',
      'Ethanol made by fermentation of glucose by yeast (no oxygen)',
      'Carboxylic acids: -COOH group; first four match alcohols',
      'Addition polymerisation: alkene monomers join, NO other product',
      'Condensation polymerisation: 2 different monomers + small molecule lost (water)',
    ],
    glossary: [
      { term: 'Alcohol', definition: 'Organic compound containing -OH functional group.' },
      { term: 'Carboxylic acid', definition: 'Organic compound containing -COOH functional group.' },
      { term: 'Addition polymerisation', definition: 'Polymer formation from alkene monomers; no by-product.' },
      { term: 'Fermentation', definition: 'Anaerobic respiration in yeast: glucose → ethanol + CO₂.' },
    ],
    practice: {
      warmUp: [
        cQ('c7-3.w1', 'Functional group of an alcohol?', ['-COOH', '-OH', 'C=C', 'C-Cl'], '-OH', 'Hydroxyl', 'Standard', 'O bonded to H'),
      ],
      level1: [
        tQ('c7-3.1', 'Vinegar contains which carboxylic acid?', 'ethanoic', '8 letters', 'CH₃COOH', 'E_______'),
      ],
      level2: [
        cQ('c7-3.2', 'In addition polymerisation:', ['Water is lost', 'No other product is formed', 'Two monomers needed', 'Catalyst made of acid'], 'No other product is formed', 'Defining feature', 'Just monomers joining', 'Versus condensation'),
      ],
    },
  }),
];

// =============================================================
// C8 — Chemical Analysis
// =============================================================
export const c8AnalysisTopics = [
  gt('gcse-c8-1', 'pure-substances-formulations', 'Pure Substances & Formulations', {
    pageRef: 'C8.1',
    intro: 'In chemistry, "pure" = single element/compound, not mixed with anything. Pure substances have SHARP melting and boiling points; mixtures melt/boil over a RANGE. A formulation is a mixture designed for a purpose with components in fixed proportions: e.g. paints, fuels, cleaning products, fertilisers, food, alloys, medicines.',
    keyPoints: [
      'Pure (chemical) = single substance only',
      'Pure substances: sharp MP/BP',
      'Impure mixtures: melt/boil over a range',
      'Formulation: deliberate mixture, components fixed quantities, designed for purpose',
    ],
    glossary: [
      { term: 'Pure substance', definition: 'Single element or compound; sharp MP/BP.' },
      { term: 'Formulation', definition: 'Mixture designed as a useful product with components in known proportions.' },
    ],
    practice: {
      warmUp: [
        cQ('c8-1.w1', 'A pure substance has:', ['A range of MPs', 'A sharp MP', 'No MP', 'Variable MP'], 'A sharp MP', 'Single value', 'Test of purity', 'Defining property'),
      ],
      level1: [
        cQ('c8-1.1', 'Which is a formulation?', ['Pure water', 'Iron', 'Paint', 'Helium'], 'Paint', 'Designed mixture', 'Multiple components', 'For purpose'),
      ],
      level2: [
        tQ('c8-1.2', 'Paracetamol tablets are a ___ — a designed mixture.', 'formulation', '11 letters', 'F__________', 'Mixture for purpose'),
      ],
    },
  }),

  gt('gcse-c8-2', 'chromatography-rf', 'Chromatography & Rf Values (RP2)', {
    pageRef: 'C8.2',
    intro: 'Paper chromatography separates dissolved coloured substances. Solvent (mobile phase) carries the dissolved substances up the paper (stationary phase). Substances that bind weakly to the paper move further. Rf = distance moved by spot ÷ distance moved by solvent (always between 0 and 1). For colourless substances we use a locating agent or UV light.',
    keyPoints: [
      'Mobile phase = solvent (water, ethanol etc.)',
      'Stationary phase = paper',
      'Rf = distance spot ÷ distance solvent',
      'Same substance always has same Rf in same solvent',
      'Pure substance: ONE spot. Mixture: multiple spots.',
    ],
    rp: {
      title: 'Required Practical: Investigate how paper chromatography can be used to separate and tell the difference between coloured substances',
      aqaRef: 'AQA Required Practical (Chemistry) 6',
      aim: 'Identify dyes in food colourings using paper chromatography.',
      method: [
        'Draw a pencil line 1.5 cm from the bottom of chromatography paper.',
        'Spot small drops of food colourings onto the line. Let dry.',
        'Place paper in beaker with shallow water (solvent must NOT touch the spots).',
        'Cover with watch glass; allow solvent to rise up the paper.',
        'Remove when solvent is near the top; mark solvent front in pencil.',
        'Calculate Rf for each spot: Rf = distance spot moved ÷ distance solvent moved.',
      ],
      safety: 'Some dyes stain skin/clothing. Beaker glass: handle with care.',
      tips: [
        'ALWAYS use pencil for the start line — pen ink would dissolve in solvent.',
        'Solvent level MUST be below spots, otherwise spots dissolve into solvent.',
      ],
    },
    glossary: [
      { term: 'Chromatography', definition: 'Method of separating substances based on their movement through a stationary phase.' },
      { term: 'Rf value', definition: 'Distance moved by spot ÷ distance moved by solvent. Always between 0 and 1.' },
    ],
    practice: {
      warmUp: [
        nQ('c8-2.w1', 'Spot moves 5 cm; solvent 10 cm. Rf?', '0.5', '5/10', 'Half', 'Decimal'),
      ],
      level1: [
        cQ('c8-2.1', 'A pure substance gives:', ['Multiple spots', 'One spot', 'No spot', 'Random spots'], 'One spot', 'Single substance', 'Single Rf', 'Test of purity'),
      ],
      level2: [
        cQ('c8-2.2', 'Why use pencil to draw the start line?', ['Looks nicer', 'Pen ink would dissolve in solvent', 'Pencil is cheaper', 'No reason'], 'Pen ink would dissolve in solvent', 'Avoids contaminating', 'Standard rule', 'Pencil insoluble'),
      ],
    },
  }),
];

// =============================================================
// C9 — Chemistry of the Atmosphere
// =============================================================
export const c9AtmosphereTopics = [
  gt('gcse-c9-1', 'atmosphere-composition-evolution', 'Atmosphere — Composition & Evolution', {
    pageRef: 'C9.1',
    intro: 'Today\'s atmosphere is approximately 78% nitrogen, 21% oxygen, 1% argon, 0.04% CO₂. Earth\'s early atmosphere came from volcanic activity — mainly CO₂, water vapour and traces of methane and ammonia. Oceans formed when water vapour condensed; CO₂ dissolved in oceans + locked in sedimentary rocks/fossil fuels. Plants evolved (~2.7 billion years ago) → photosynthesis released O₂; the modern oxygen-rich atmosphere built up over hundreds of millions of years.',
    keyPoints: [
      'Today: 78% N₂, 21% O₂, 1% Ar, 0.04% CO₂',
      'Early atmosphere: from volcanoes (CO₂, water vapour, traces of CH₄, NH₃)',
      'Oceans formed from condensing water vapour',
      'CO₂ removed by: dissolving in oceans, sedimentary rocks, fossil fuels, plants',
      'O₂ added by photosynthesis from plants and algae',
    ],
    glossary: [
      { term: 'Photosynthesis', definition: 'Plants use light to make glucose from CO₂ and H₂O; releases O₂.' },
    ],
    practice: {
      warmUp: [
        nQ('c9-1.w1', '% nitrogen in today\'s atmosphere?', '78', 'Most abundant gas', 'Around 78%', '2 digits'),
      ],
      level1: [
        cQ('c9-1.1', 'Earth\'s early atmosphere contained mostly:', ['Oxygen', 'Carbon dioxide and water vapour', 'Nitrogen', 'Argon'], 'Carbon dioxide and water vapour', 'From volcanoes', 'Greenhouse gases', 'Pre-life'),
      ],
      level2: [
        tQ('c9-1.2', 'Oxygen built up because of ___.', 'photosynthesis', '14 letters', 'Plants and algae', 'P_____________'),
      ],
    },
  }),

  gt('gcse-c9-2', 'climate-change-greenhouse', 'Climate Change & Greenhouse Effect', {
    pageRef: 'C9.2',
    intro: 'Greenhouse gases (CO₂, methane CH₄, water vapour) absorb infrared radiation re-emitted by Earth\'s surface, warming the lower atmosphere — without them Earth would be too cold for life. Human activities (burning fossil fuels, deforestation, agriculture, livestock) raise CO₂ + CH₄ levels → enhanced greenhouse effect → global warming → climate change. Effects: rising sea levels, melting ice caps, more extreme weather, biodiversity loss.',
    keyPoints: [
      'Greenhouse gases: CO₂, CH₄, water vapour',
      'Mechanism: absorb IR re-emitted by Earth → trap heat in atmosphere',
      'Human increase: fossil fuels (CO₂), agriculture/livestock (CH₄), deforestation',
      'Effects: rising sea levels, ice melt, extreme weather, biodiversity loss',
      'Carbon footprint: total CO₂ + other greenhouse gases over a product/service\'s life',
    ],
    glossary: [
      { term: 'Greenhouse effect', definition: 'Trapping of heat near Earth\'s surface by greenhouse gases.' },
      { term: 'Carbon footprint', definition: 'Total greenhouse gas emissions from a product, service or person.' },
    ],
    examQ: {
      prompt: 'Explain how burning fossil fuels contributes to global warming. (4 marks)',
      maxMarks: 4,
      markSchemePoints: [
        'Burning fossil fuels releases carbon dioxide (CO₂)',
        'CO₂ is a greenhouse gas',
        'CO₂ absorbs infrared radiation re-emitted by Earth\'s surface',
        'More CO₂ → more heat trapped → mean global temperatures rise',
      ],
    },
    examinerInsights: {
      whyMarksLost: [
        'Vague language: "fossil fuels are bad for the environment" earns ZERO. Say WHAT they release and HOW it warms.',
        'Confusing the GREENHOUSE effect (natural and necessary) with the ENHANCED greenhouse effect (man-made warming).',
        'Forgetting to mention infrared radiation — that\'s the specific mechanism examiners want to see.',
      ],
      whatExaminersWant: [
        'A causal chain: combustion → CO₂ → IR absorption → temperature rise.',
        'Use of "absorbs infrared radiation" verbatim — this exact phrase is on most mark schemes.',
        'Linkage between "more CO₂" and "more heat trapped" — examiners pay for the WORD "more" in 4-mark questions.',
      ],
      commonTraps: [
        'Saying CO₂ "destroys the ozone layer" — that\'s CFCs, not CO₂.',
        'Saying CO₂ "stops sunlight reaching the Earth" — actually it stops re-emitted IR LEAVING.',
        'Listing other pollutants (NOx, SOx) — the question is about global warming, not air quality.',
      ],
      gradeNineMove: 'In 4+ mark questions, write the answer as a numbered chain: 1. CO₂ released by combustion. 2. CO₂ is a greenhouse gas. 3. Absorbs IR re-emitted by Earth. 4. Atmosphere warms. Examiners can tick each step without hunting for it.',
    },
    practice: {
      warmUp: [
        cQ('c9-2.w1', 'Which is a greenhouse gas?', ['Oxygen', 'Nitrogen', 'Methane', 'Argon'], 'Methane', 'CH₄', 'Cattle + landfill', 'Strong greenhouse'),
      ],
      level1: [
        cQ('c9-2.1', 'Greenhouse gases work by:', ['Reflecting sunlight', 'Absorbing IR re-emitted by Earth', 'Cooling the atmosphere', 'Producing oxygen'], 'Absorbing IR re-emitted by Earth', 'Trap re-emitted heat', 'Mechanism', 'IR absorption'),
      ],
      level2: [
        tQ('c9-2.2', 'The total greenhouse-gas emissions from a person or product is its carbon ___.', 'footprint', '9 letters', 'F________', 'Total CO₂e'),
      ],
    },
  }),
];

// =============================================================
// C10 — Using Resources
// =============================================================
export const c10UsingResourcesTopics = [
  gt('gcse-c10-1', 'finite-renewable-lca', 'Finite & Renewable Resources, LCA', {
    pageRef: 'C10.1',
    intro: 'Finite resources: cannot be replaced as fast as we use them (fossil fuels, metal ores). Renewable: form/replace at the rate they\'re used (wood, water from rain). Sustainable development = meeting needs of today without compromising future generations. Life Cycle Assessment (LCA) evaluates environmental impact at 4 stages: raw materials, manufacture/packaging, use, disposal/recycling.',
    keyPoints: [
      'Finite: cannot be replenished in human timescales',
      'Renewable: replaced naturally at usage rate',
      'Sustainable development concept',
      'LCA stages: raw materials → manufacture → use → disposal',
      'Some LCA values (e.g. CO₂) are objective; some (e.g. visual pollution) subjective',
    ],
    glossary: [
      { term: 'Finite resource', definition: 'A resource that cannot be replenished within a human lifetime.' },
      { term: 'Life Cycle Assessment (LCA)', definition: 'Analysis of the environmental impact of a product across its life.' },
      { term: 'Sustainable', definition: 'Meeting current needs without harming the ability of future generations to meet theirs.' },
    ],
    practice: {
      warmUp: [
        cQ('c10-1.w1', 'Which is finite?', ['Wood', 'Iron ore', 'Wind', 'Solar energy'], 'Iron ore', 'Mineral', 'Mined', 'Cannot replenish'),
      ],
      level1: [
        cQ('c10-1.1', 'LCA includes which stage?', ['Manufacture only', 'Use only', 'Raw → manufacture → use → disposal', 'Marketing only'], 'Raw → manufacture → use → disposal', 'Whole life', 'Comprehensive', 'Cradle to grave'),
      ],
      level2: [
        cQ('c10-1.2', 'Why is "visual pollution" hard to use in LCA?', ['Always positive', 'Subjective opinion', 'Easy to measure', 'Always negative'], 'Subjective opinion', 'Personal view', 'Hard to quantify', 'Bias risk'),
      ],
    },
  }),

  gt('gcse-c10-2', 'potable-water-treatment', 'Potable Water & Waste Water Treatment', {
    pageRef: 'C10.2',
    intro: 'Potable water = safe to drink. UK: collect rainwater/groundwater → filter (removes solid particles) → sterilise (kill bacteria, e.g. by chlorine, UV, ozone). In hot/dry countries: desalination by reverse osmosis or distillation (energy-intensive). Waste water (sewage + industrial) is treated: screening (large debris removal) → settling (solids settle as sludge) → aerobic biological treatment of effluent → anaerobic digestion of sludge (produces methane fuel).',
    keyPoints: [
      'Potable: safe to drink (low salt + low microbes; not pure)',
      'UK water: filter + sterilise (chlorine / UV / ozone)',
      'Desalination: removes salt; energy-intensive',
      'Waste-water: screening → settling → aerobic + anaerobic digestion',
      'Sludge → anaerobic digestion → methane (renewable fuel)',
    ],
    glossary: [
      { term: 'Potable water', definition: 'Water safe for humans to drink.' },
      { term: 'Sterilisation', definition: 'Killing harmful microorganisms.' },
      { term: 'Desalination', definition: 'Removing salt from sea water to make potable water.' },
    ],
    practice: {
      warmUp: [
        cQ('c10-2.w1', 'Potable water means:', ['Pure', 'Safe to drink', 'Boiled', 'Filtered only'], 'Safe to drink', 'Drinkable', 'Low contamination', 'Standard'),
      ],
      level1: [
        cQ('c10-2.1', 'Two stages of UK drinking water treatment:', ['Mining + smelting', 'Filtration + sterilisation', 'Boiling + freezing', 'Distillation only'], 'Filtration + sterilisation', 'Standard', 'Solids + bacteria', 'Twin process'),
      ],
      level2: [
        cQ('c10-2.2', 'Why is desalination not used widely in UK?', ['Too cheap', 'Energy-intensive; UK has plenty of fresh water', 'Illegal', 'No salt water'], 'Energy-intensive; UK has plenty of fresh water', 'Cost-benefit', 'Local resource availability', 'Practical'),
      ],
    },
  }),

  gt('gcse-c10-3', 'haber-process-fertilisers', 'Haber Process & Fertilisers', {
    pageRef: 'C10.3',
    tier: 'higher',
    intro: 'The Haber process makes ammonia (NH₃) from N₂ + H₂. Conditions: 450°C, 200 atm, iron catalyst — a compromise between yield (favoured by lower T, higher P, by Le Chatelier) and rate (favoured by higher T). Equation: N₂ + 3H₂ ⇌ 2NH₃ (reversible). Ammonia is used to make fertilisers (NPK = nitrogen + phosphorus + potassium for crop growth) and nitric acid. Industrial scale + agricultural impact.',
    keyPoints: [
      'Haber process: N₂ + 3H₂ ⇌ 2NH₃',
      'Conditions: 450°C, 200 atm, iron catalyst',
      'Compromise between yield (low T, high P) and rate (high T)',
      'NH₃ → nitric acid → ammonium nitrate fertiliser',
      'NPK fertilisers contain N, P, K — essential for plant growth',
    ],
    glossary: [
      { term: 'Haber process', definition: 'Industrial production of ammonia from nitrogen and hydrogen.' },
      { term: 'NPK fertiliser', definition: 'Fertiliser providing nitrogen, phosphorus and potassium.' },
    ],
    practice: {
      warmUp: [
        tQ('c10-3.w1', 'Catalyst used in the Haber process is ___.', 'iron', '4 letters', 'Fe', 'I___'),
        nQ('c10-3.w2', 'Pressure used in Haber process (atm)?', '200', '~200', 'High pressure', '3 digits'),
      ],
      level1: [
        cQ('c10-3.1', 'Why use 450°C and not a lower temperature?', ['Yield is higher', 'Rate would be too slow at low T', 'Catalyst breaks', 'No reason'], 'Rate would be too slow at low T', 'Compromise', 'Higher T = faster', 'Le Chatelier vs kinetics'),
      ],
      level2: [
        cQ('c10-3.2', 'NPK fertiliser supplies:', ['Iron, copper, zinc', 'Nitrogen, phosphorus, potassium', 'Carbon, oxygen, hydrogen', 'Sodium, potassium, calcium'], 'Nitrogen, phosphorus, potassium', 'Standard', 'Essential macronutrients', 'For crops'),
      ],
    },
  }),
];

// =============================================================
// Section bundles
// =============================================================
export const allChemistryGCSEBundles = {
  c1: c1AtomicStructureTopics,
  c2: c2BondingTopics,
  c3: c3QuantitativeTopics,
  c4: c4ChemicalChangesTopics,
  c5: c5EnergyChangesTopics,
  c6: c6RateExtentTopics,
  c7: c7OrganicTopics,
  c8: c8AnalysisTopics,
  c9: c9AtmosphereTopics,
  c10: c10UsingResourcesTopics,
};
